import { useEffect, useMemo, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import {
  Table, TableBody, TableCell, TableHead, TableHeader, TableRow,
} from "@/components/ui/table";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { Plus, Pencil, Trash2, Shield, Search } from "lucide-react";
import { toast } from "sonner";
import MultiSelect from "@/components/MultiSelect";
import UploadExcelButton from "@/components/UploadExcelButton";
import { FbmStatCell, FbmStatCellGraded } from "@/components/FbmStat";
import { Link } from "react-router-dom";

const emptyForm = { name: "", country: "", league_ids: [], position_in_table: "" };

export default function TeamsPage({ basePath = "/admin", canMutate = true }) {
  const { user } = useAuth();
  const ignoreFriendlies = user?.ignore_friendlies ?? true;
  const [teams, setTeams] = useState([]);
  const [leagues, setLeagues] = useState([]);
  const [fbm, setFbm] = useState({});
  const [q, setQ] = useState("");
  const [filterLeagueId, setFilterLeagueId] = useState("");
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyForm);
  const [saving, setSaving] = useState(false);
  const [toDelete, setToDelete] = useState(null);

  const load = async (query = "") => {
    setLoading(true);
    try {
      const [t, l] = await Promise.all([
        api.get("/teams", { params: query ? { q: query } : {} }),
        api.get("/leagues"),
      ]);
      setTeams(t.data); setLeagues(l.data);
    } catch (e) { toast.error(formatApiError(e)); }
    finally { setLoading(false); }
  };

  useEffect(() => { load(); }, []);
  useEffect(() => {
    const t = setTimeout(() => load(q), 250);
    return () => clearTimeout(t);
  }, [q]);

  const visibleTeams = useMemo(() => {
    if (!filterLeagueId) return teams;
    return teams.filter((t) => (t.league_ids || []).includes(filterLeagueId));
  }, [teams, filterLeagueId]);

  // Fetch FBM for the visible teams whenever they change or ignore_friendlies toggles.
  useEffect(() => {
    const ids = visibleTeams.map((t) => t.id);
    if (!ids.length) { setFbm({}); return; }
    const csv = ids.join(",");
    let alive = true;
    api.get(`/fbm/teams-bulk`, { params: { ids: csv } })
      .then((r) => { if (alive) setFbm(r.data || {}); })
      .catch(() => {});
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [visibleTeams.map((t) => t.id).join(","), ignoreFriendlies]);

  const leagueOptions = leagues.map((l) => ({ value: l.id, label: l.name }));

  const openCreate = () => { setEditing(null); setForm(emptyForm); setDialogOpen(true); };
  const openEdit = (t) => {
    setEditing(t);
    setForm({
      name: t.name,
      country: t.country,
      league_ids: t.league_ids || [],
      position_in_table: t.position_in_table == null ? "" : String(t.position_in_table),
    });
    setDialogOpen(true);
  };

  const onSubmit = async (e) => {
    e.preventDefault();
    if (form.league_ids.length === 0) { toast.error("Pick at least one league"); return; }
    const pit = form.position_in_table === "" ? null : parseInt(form.position_in_table, 10);
    if (pit != null && (Number.isNaN(pit) || pit < 1 || pit > 30)) {
      toast.error("Position in table must be 1–30"); return;
    }
    setSaving(true);
    try {
      const payload = { name: form.name.trim(), country: form.country.trim(), league_ids: form.league_ids, position_in_table: pit };
      if (editing) {
        await api.patch(`/teams/${editing.id}`, payload);
        toast.success("Team updated");
      } else {
        await api.post("/teams", payload);
        toast.success("Team created");
      }
      setDialogOpen(false); await load(q);
    } catch (err) { toast.error(formatApiError(err)); }
    finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!toDelete) return;
    try {
      await api.delete(`/teams/${toDelete.id}`);
      toast.success("Team deleted"); setToDelete(null); await load(q);
    } catch (err) { toast.error(formatApiError(err)); setToDelete(null); }
  };

  return (
    <div data-testid="teams-page">
      <header className="flex flex-wrap items-end justify-between gap-4 mb-8">
        <div>
          <div className="text-xs uppercase tracking-[0.25em] text-muted-foreground mb-2">Clubs</div>
          <h1 className="font-display uppercase text-4xl md:text-5xl font-bold tracking-tight">Teams</h1>
          <p className="text-muted-foreground mt-2 max-w-lg">Clubs and squads, attached to one or more leagues.</p>
        </div>
        <Button onClick={openCreate} disabled={leagues.length === 0 || !canMutate} className={`h-11 rounded-none font-display uppercase tracking-[0.18em] ${canMutate ? "" : "hidden"}`} data-testid="create-team-button">
          <Plus className="h-4 w-4 mr-2" /> New Team
        </Button>
      </header>

      {leagues.length === 0 && !loading && canMutate && (
        <div className="rounded-xl border border-border bg-card p-6 mb-6 text-sm text-muted-foreground">
          Create at least one league before adding teams.
        </div>
      )}

      <div className="flex flex-wrap items-end gap-3 mb-4">
        <div className="relative max-w-sm flex-1 min-w-[240px]">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search teams…" className="pl-9" data-testid="teams-search-input" />
        </div>
        <div className="min-w-[220px]">
          <Label className="text-[10px] uppercase tracking-[0.18em] text-muted-foreground">League</Label>
          <Select
            value={filterLeagueId || "__all__"}
            onValueChange={(v) => setFilterLeagueId(v === "__all__" ? "" : v)}
          >
            <SelectTrigger data-testid="teams-league-filter">
              <SelectValue placeholder="All leagues" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All leagues</SelectItem>
              {leagues.map((l) => (
                <SelectItem key={l.id} value={l.id} data-testid={`teams-league-filter-option-${l.id}`}>
                  {l.name}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      <div className="rounded-xl border border-border bg-card overflow-hidden shadow-sm">
        <Table data-testid="teams-table">
          <TableHeader>
            <TableRow className="hover:bg-transparent border-border">
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Name</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px]">Country</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right" title="Position in league table">Pos</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Overall</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Finishing</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Passing</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Defending</TableHead>
              <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">FBM</TableHead>
              {canMutate && <TableHead className="px-6 py-4 font-display uppercase tracking-[0.15em] text-[11px] text-right">Actions</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading ? (
              <TableRow><TableCell colSpan={canMutate ? 9 : 8} className="px-6 py-12 text-center text-muted-foreground">Loading…</TableCell></TableRow>
            ) : visibleTeams.length === 0 ? (
              <TableRow><TableCell colSpan={canMutate ? 9 : 8} className="px-6 py-16 text-center">
                <Shield className="h-8 w-8 mx-auto mb-3 text-muted-foreground" />
                <div className="font-display uppercase tracking-wider">No teams</div>
              </TableCell></TableRow>
            ) : (
              visibleTeams.map((t) => {
                const f = fbm[t.id];
                return (
                  <TableRow key={t.id} className="tr-fade border-border hover:bg-muted/30" data-testid={`team-row-${t.id}`}>
                    <TableCell className="px-6 py-4 font-medium">
                      <Link to={`${basePath}/teams/${t.id}`} className="hover:underline" data-testid={`team-link-${t.id}`}>{t.name}</Link>
                    </TableCell>
                    <TableCell className="px-6 py-4 text-muted-foreground text-sm">{t.country || "—"}</TableCell>
                    <TableCell className="px-6 py-4 text-right font-mono" data-testid={`team-position-${t.id}`}>
                      {t.position_in_table == null ? <span className="text-muted-foreground">—</span> : t.position_in_table}
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <FbmStatCellGraded value={f?.overall} testId={`team-overall-${t.id}`} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <FbmStatCellGraded value={f?.finishing} testId={`team-finishing-${t.id}`} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <FbmStatCellGraded value={f?.passing} testId={`team-passing-${t.id}`} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right">
                      <FbmStatCellGraded value={f?.defending} testId={`team-defending-${t.id}`} />
                    </TableCell>
                    <TableCell className="px-6 py-4 text-right font-mono text-sm" data-testid={`team-fbm-${t.id}`}>
                      {f?.fbm_score == null ? <span className="text-muted-foreground">—</span> : Math.ceil(Number(f.fbm_score))}
                    </TableCell>
                    {canMutate && (
                      <TableCell className="px-6 py-4 text-right">
                        <UploadExcelButton url={`/teams/${t.id}/import-excel`} testId={`upload-team-${t.id}`} onResult={load} />
                        <Button size="sm" variant="ghost" onClick={() => openEdit(t)} data-testid={`edit-team-${t.id}`}><Pencil className="h-4 w-4" /></Button>
                        <Button size="sm" variant="ghost" onClick={() => setToDelete(t)} data-testid={`delete-team-${t.id}`}><Trash2 className="h-4 w-4 text-destructive" /></Button>
                      </TableCell>
                    )}
                  </TableRow>
                );
              })
            )}
          </TableBody>
        </Table>
      </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="sm:max-w-md" data-testid="team-dialog">
          <DialogHeader>
            <DialogTitle className="font-display uppercase tracking-tight text-2xl">{editing ? "Edit Team" : "New Team"}</DialogTitle>
            <DialogDescription>A team can be attached to multiple leagues.</DialogDescription>
          </DialogHeader>
          <form onSubmit={onSubmit} className="space-y-4">
            <div className="space-y-2">
              <Label htmlFor="team-name">Name</Label>
              <Input id="team-name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} required data-testid="team-name-input" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-country">Country</Label>
              <Input id="team-country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} required data-testid="team-country-input" />
            </div>
            <div className="space-y-2">
              <Label>Leagues</Label>
              <MultiSelect
                options={leagueOptions}
                value={form.league_ids}
                onChange={(v) => setForm({ ...form, league_ids: v })}
                placeholder="Pick one or more leagues…"
                testIdPrefix="team-leagues"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="team-position">Position in table <span className="text-muted-foreground font-normal">(1–30, optional)</span></Label>
              <Input
                id="team-position"
                type="number"
                min="1"
                max="30"
                value={form.position_in_table}
                onChange={(e) => setForm({ ...form, position_in_table: e.target.value })}
                placeholder="e.g. 16"
                data-testid="team-position-input"
              />
              <p className="text-xs text-muted-foreground">Used by the FBM model to weight defensive scores.</p>
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={saving} data-testid="team-save-button">{saving ? "Saving…" : editing ? "Save changes" : "Create"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!toDelete} onOpenChange={(o) => !o && setToDelete(null)}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete team?</AlertDialogTitle>
            <AlertDialogDescription>This will permanently delete <strong>{toDelete?.name}</strong>. Players or matches referencing it must be detached first.</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={confirmDelete} className="bg-destructive text-destructive-foreground hover:bg-destructive/90" data-testid="delete-team-confirm-button">Delete</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
